import {
  createManualFindingObservation,
  findManualFinding,
} from './observationUtils';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { messages } from './messages';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useCreateObservationFromManualFindingMutation } from '../../../../../graphql/mutations/__generated__/CreateObservationFromManualFinding.graphql';
import { useActiveProjectId, useServiceAuth } from '../../../../../hooks';
import { useSelector } from 'react-redux';
import { userSelectors } from '../../../../../redux/modules/user';
import { useUpdateObservationMutation } from '../../../../../graphql/mutations/__generated__/UpdateObservation.graphql';
import { ObservationIn } from '../../../../../util/graphql/api.graphql';
import { ObservationWithTask } from './types';

interface ManualFindingProps {
  subjectId: string;
  observation: fhir.Observation;
  handleObservationCreation: (observation: ObservationWithTask) => void;
  handleObservationUpdate: (observation: ObservationWithTask) => void;
}

export const ManualFinding = (props: ManualFindingProps) => {
  const { formatMessage } = useIntl();
  const datasetId = useActiveProjectId();
  const serviceAuth = useServiceAuth();
  const user = useSelector(userSelectors.selectActiveUser);

  const {
    observation,
    subjectId,
    handleObservationCreation,
    handleObservationUpdate,
  } = props;

  const context = useMemo(
    () => ({
      headers: {
        'LifeOmic-Account': serviceAuth.account,
        Authorization: `Bearer ${serviceAuth.authToken}`,
      },
    }),
    [serviceAuth?.account, serviceAuth?.authToken],
  );

  const [createObservation] = useCreateObservationFromManualFindingMutation({
    context,
  });
  const [updateObservation] = useUpdateObservationMutation({ context });

  const [finding, setFinding] = React.useState(
    findManualFinding(observation) || '',
  );
  const dirty = React.useRef(false);

  const handleMutation = React.useCallback(() => {
    if (observation.status === 'final' || !dirty.current) {
      return;
    }

    if (!observation.id) {
      createObservation({
        variables: {
          user: user.id,
          effectiveDateTime: new Date().toISOString(),
          datasetId,
          subjectRef: subjectId,
          finding,
        },
      }).then((result: any) =>
        handleObservationCreation(result.data.createObservation),
      );
    } else {
      const updatedObservation = createManualFindingObservation(
        observation.id,
        subjectId,
        datasetId,
        finding,
      );
      updateObservation({
        variables: {
          observation: updatedObservation as ObservationIn,
        },
      }).then((result: any) =>
        handleObservationUpdate(result.data.updateObservation),
      );
    }

    dirty.current = false;
  }, [
    dirty,
    finding,
    observation,
    updateObservation,
    createObservation,
    datasetId,
    handleObservationCreation,
    handleObservationUpdate,
    subjectId,
    user?.id,
  ]);

  const onChange = React.useCallback(
    (event: any) => {
      setFinding(event.target.value);
      dirty.current = true;
    },
    [setFinding],
  );

  useEffect(
    () => setFinding(findManualFinding(observation) || ''),
    [observation],
  );

  return (
    <>
      {observation.status === 'registered' && (
        <TextArea
          label={formatMessage(messages.addNewFindingLabel)}
          value={finding}
          fullWidth={true}
          onChange={onChange}
          onBlur={() => handleMutation()}
        />
      )}
      {['preliminary', 'final'].includes(observation.status) && (
        <Text>{finding}</Text>
      )}
    </>
  );
};
